/**
 * Create/edit drawer for a per-tenant RoutingPolicy (ADR-126 Decision 4).
 * Binds the policy to the current scope (org default or site
 * override) and lets the admin set the when_state condition, the adjustment,
 * an optional alert-type scope, priority, and active flag.
 */

import { useEffect } from 'react';
import { Field, SimpleGrid, Switch, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DrawerComponent,
  InputField,
  SelectInput,
} from '@themes/aegix';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  routingPolicyFormSchema,
  type RoutingPolicyFormValues,
} from '../../../validations/whakahaereConfig.validation';
import {
  OPERATING_CONDITION_LABELS,
  ROUTING_ADJUSTMENT_LABELS,
  WHEN_EVENT_LABELS,
  type RoutingPolicy,
  type OperatingCondition,
  type RoutingAdjustment,
  type WhenEvent,
} from '../../../services';
import {
  useCreateRoutingPolicy,
  useUpdateRoutingPolicy,
  useAlertTypes,
} from '../../../hooks/queries';
import type { ConfigScope } from '../types';
import { toaster } from '../../ui/toaster';

interface RoutingPolicyDrawerProps {
  scope: ConfigScope;
  isOpen: boolean;
  row: RoutingPolicy | null;
  onClose: () => void;
  onDelete: (id: number) => void;
}

const WHEN_OPTIONS = (
  Object.keys(OPERATING_CONDITION_LABELS) as OperatingCondition[]
).map((c) => ({ value: c, label: OPERATING_CONDITION_LABELS[c] }));

const WHEN_EVENT_OPTIONS = (
  Object.keys(WHEN_EVENT_LABELS) as WhenEvent[]
).map((e) => ({ value: e, label: WHEN_EVENT_LABELS[e] }));

const ADJUSTMENT_OPTIONS = (
  Object.keys(ROUTING_ADJUSTMENT_LABELS) as RoutingAdjustment[]
).map((a) => ({ value: a, label: ROUTING_ADJUSTMENT_LABELS[a] }));

const ALL_ALERTS_VALUE = '__all__';

function defaults(row: RoutingPolicy | null): RoutingPolicyFormValues {
  return {
    name: row?.name ?? '',
    district: row?.district ?? null,
    school: row?.school ?? null,
    alert_type: row?.alert_type ?? null,
    when_state: row?.when_state ?? 'not_operating',
    when_event: row?.when_event ?? 'any',
    adjustment: row?.adjustment ?? 'restrict_to_on_duty',
    priority: row?.priority ?? 100,
    is_active: row?.is_active ?? true,
  };
}

export function RoutingPolicyDrawer({
  scope,
  isOpen,
  row,
  onClose,
  onDelete,
}: RoutingPolicyDrawerProps) {
  const createPolicy = useCreateRoutingPolicy();
  const updatePolicy = useUpdateRoutingPolicy();

  // Alert types for the optional scope dropdown (scoped to the site when one
  // is selected, else the org-wide set).
  const { data: alertTypeData } = useAlertTypes(
    scope.schoolId ? { school: scope.schoolId, page_size: 200 } : { page_size: 200 },
  );
  const alertTypeOptions = [
    { value: ALL_ALERTS_VALUE, label: 'All alerts' },
    ...(alertTypeData?.results ?? []).map((t) => ({
      value: String(t.id),
      label: t.name,
    })),
  ];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RoutingPolicyFormValues>({
    defaultValues: defaults(row),
    resolver: zodResolver(routingPolicyFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isOpen) reset(defaults(row));
  }, [isOpen, row, reset]);

  const onSubmit = async (values: RoutingPolicyFormValues) => {
    // Bind to the current scope's attachment tier (org XOR site).
    const payload = {
      name: values.name ?? '',
      district: scope.schoolId ? null : scope.districtId,
      school: scope.schoolId ?? null,
      alert_type: values.alert_type ?? null,
      when_state: values.when_state as OperatingCondition,
      when_event: values.when_event as WhenEvent,
      adjustment: values.adjustment as RoutingAdjustment,
      priority: values.priority,
      is_active: values.is_active,
    };
    try {
      if (row) {
        await updatePolicy.mutateAsync({ id: row.id, data: payload });
        toaster.create({ title: 'Policy updated', type: 'success' });
      } else {
        await createPolicy.mutateAsync(payload);
        toaster.create({ title: 'Policy created', type: 'success' });
      }
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not save the policy',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={row ? 'Edit policy' : 'Add policy'}
      size="md"
      closeOnOutside={false}
      footer={
        <SimpleGrid columns={row ? 3 : 2} gap={3} width="100%">
          {row && (
            <ButtonComponent
              visual="alert"
              type="button"
              onClick={() => onDelete(row.id)}
              data-e2e="delete-routing-policy-button"
            >
              Delete
            </ButtonComponent>
          )}
          <ButtonComponent visual="secondary" type="button" onClick={onClose}>
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="submit"
            form="routing-policy-form"
            disabled={isSubmitting}
            data-e2e="save-routing-policy-button"
          >
            Save
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <form id="routing-policy-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.name}>
            <Field.Label htmlFor="policy-name">Name</Field.Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputField
                  id="policy-name"
                  placeholder="e.g. Closed → on-duty only"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="policy-name-input"
                />
              )}
            />
            {errors.name && (
              <Field.ErrorText>{errors.name.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.when_state}>
            <Field.Label htmlFor="policy-when">When the site is…</Field.Label>
            <Controller
              name="when_state"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="policy-when"
                  value={field.value}
                  option={WHEN_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="policy-when-select"
                />
              )}
            />
            {errors.when_state && (
              <Field.ErrorText>{errors.when_state.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.when_event}>
            <Field.Label htmlFor="policy-when-event">
              …and the scheduled-event state is…
            </Field.Label>
            <Controller
              name="when_event"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="policy-when-event"
                  value={field.value}
                  option={WHEN_EVENT_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="policy-when-event-select"
                />
              )}
            />
            {errors.when_event && (
              <Field.ErrorText>{errors.when_event.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.adjustment}>
            <Field.Label htmlFor="policy-adjustment">Then…</Field.Label>
            <Controller
              name="adjustment"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="policy-adjustment"
                  value={field.value}
                  option={ADJUSTMENT_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="policy-adjustment-select"
                />
              )}
            />
            {errors.adjustment && (
              <Field.ErrorText>{errors.adjustment.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.alert_type}>
            <Field.Label htmlFor="policy-alert-type">Alert type</Field.Label>
            <Controller
              name="alert_type"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="policy-alert-type"
                  value={field.value == null ? ALL_ALERTS_VALUE : String(field.value)}
                  option={alertTypeOptions}
                  onChange={(val: string) =>
                    field.onChange(val === ALL_ALERTS_VALUE ? null : Number(val))
                  }
                  searchable
                  e2eTitle="policy-alert-type-select"
                />
              )}
            />
            {errors.alert_type && (
              <Field.ErrorText>{errors.alert_type.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.priority}>
            <Field.Label htmlFor="policy-priority">
              Priority (lower wins)
            </Field.Label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <InputField
                  id="policy-priority"
                  type="number"
                  min={0}
                  value={String(field.value)}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? 0 : Number(e.target.value),
                    )
                  }
                  data-e2e="policy-priority-input"
                />
              )}
            />
            {errors.priority && (
              <Field.ErrorText>{errors.priority.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <Switch.Root
                  checked={field.value}
                  onCheckedChange={(d) => field.onChange(d.checked)}
                >
                  <Switch.HiddenInput data-e2e="policy-active-switch" />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <Switch.Label>Active</Switch.Label>
                </Switch.Root>
              )}
            />
          </Field.Root>
        </VStack>
      </form>
    </DrawerComponent>
  );
}
